import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Link2,
  Plus,
  Edit,
  Trash2,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  AlertCircle,
} from 'lucide-react';
import { LinkModal, SharedLink, LinkFormData } from '../modals/LinkModal';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';
import * as LucideIcons from 'lucide-react';
import * as SimpleIcons from 'react-icons/si';
import * as FontAwesome from 'react-icons/fa';
import * as MaterialDesign from 'react-icons/md';

export function LinksManagement() {
  const confirm = useConfirm();
  const { profile } = useAuth();
  const [links, setLinks] = useState<SharedLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState<SharedLink | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadLinks();
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel('shared_links_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shared_links',
        },
        () => {
          loadLinks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('shared_links')
        .select(`
          *,
          link_course_assignments(
            course_id,
            courses(name, description)
          )
        `)
        .order('orden', { ascending: true });

      if (error) throw error;
      setLinks(data || []);
    } catch (error) {
      console.error('Error loading links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData: LinkFormData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      let linkId: string;

      if (editingLink) {
        // Update existing link
        const { error } = await supabase
          .from('shared_links')
          .update({
            titulo: formData.titulo,
            descripcion: formData.descripcion || null,
            url: formData.url,
            icono: formData.icono,
            activo: formData.activo,
            apply_to_all_courses: formData.apply_to_all_courses,
          })
          .eq('id', editingLink.id);

        if (error) throw error;
        linkId = editingLink.id;

        // Delete existing course assignments
        const { error: deleteError } = await supabase
          .from('link_course_assignments')
          .delete()
          .eq('link_id', linkId);

        if (deleteError) throw deleteError;
      } else {
        // Insert new link
        const maxOrden = links.length > 0 ? Math.max(...links.map(l => l.orden)) : -1;
        const { data: newLink, error } = await supabase
          .from('shared_links')
          .insert({
            titulo: formData.titulo,
            descripcion: formData.descripcion || null,
            url: formData.url,
            icono: formData.icono,
            activo: formData.activo,
            apply_to_all_courses: formData.apply_to_all_courses,
            orden: maxOrden + 1,
            creado_por: user.id,
          })
          .select()
          .single();

        if (error) throw error;
        if (!newLink) throw new Error('No se pudo crear el link');
        linkId = newLink.id;
      }

      // Insert course assignments if not global
      if (!formData.apply_to_all_courses && formData.selectedCourses.length > 0) {
        const assignments = formData.selectedCourses.map(courseId => ({
          link_id: linkId,
          course_id: courseId,
        }));

        const { error: assignError } = await supabase
          .from('link_course_assignments')
          .insert(assignments);

        if (assignError) throw assignError;
      }

      setShowModal(false);
      setEditingLink(null);
      loadLinks();
    } catch (error) {
      console.error('Error saving link:', error);
      throw error;
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Eliminar link',
      description: '¿Seguro que deseas eliminar este link? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;

    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('shared_links')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Link eliminado');
      loadLinks();
    } catch (error: any) {
      console.error('Error deleting link:', error);
      toast.error('No se pudo eliminar el link');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (link: SharedLink) => {
    try {
      const { error } = await supabase
        .from('shared_links')
        .update({ activo: !link.activo })
        .eq('id', link.id);

      if (error) throw error;
      loadLinks();
    } catch (error) {
      console.error('Error toggling link active state:', error);
    }
  };

  const handleMoveUp = async (link: SharedLink, index: number) => {
    if (index === 0) return;

    try {
      const prevLink = links[index - 1];

      await supabase
        .from('shared_links')
        .update({ orden: prevLink.orden })
        .eq('id', link.id);

      await supabase
        .from('shared_links')
        .update({ orden: link.orden })
        .eq('id', prevLink.id);

      loadLinks();
    } catch (error) {
      console.error('Error moving link up:', error);
    }
  };

  const handleMoveDown = async (link: SharedLink, index: number) => {
    if (index === links.length - 1) return;

    try {
      const nextLink = links[index + 1];

      await supabase
        .from('shared_links')
        .update({ orden: nextLink.orden })
        .eq('id', link.id);

      await supabase
        .from('shared_links')
        .update({ orden: link.orden })
        .eq('id', nextLink.id);

      loadLinks();
    } catch (error) {
      console.error('Error moving link down:', error);
    }
  };

  const handleEdit = (link: SharedLink) => {
    setEditingLink(link);
    setShowModal(true);
  };

  const handleNewLink = () => {
    setEditingLink(null);
    setShowModal(true);
  };

  const getIcon = (iconString: string) => {
    const [library, iconName] = iconString.includes(':')
      ? iconString.split(':')
      : ['lucide', iconString];

    try {
      if (library === 'si') {
        return (SimpleIcons as any)[iconName] || LucideIcons.Link;
      } else if (library === 'fa') {
        return (FontAwesome as any)[iconName] || LucideIcons.Link;
      } else if (library === 'md') {
        return (MaterialDesign as any)[iconName] || LucideIcons.Link;
      } else {
        return (LucideIcons as any)[iconName] || LucideIcons.Link;
      }
    } catch {
      return LucideIcons.Link;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cubus-lime"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-display text-3xl font-bold text-white mb-2">Gestión de Links</h1>
          <p className="text-muted">Administra los links externos compartidos con los estudiantes</p>
        </div>
        <button
          onClick={handleNewLink}
          className="flex items-center gap-2 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg font-semibold rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Agregar Link
        </button>
      </div>

      {links.length === 0 ? (
        <div className="glass-surface rounded-xl p-12 border border-white/[0.06] text-center">
          <Link2 className="w-16 h-16 text-faint mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-muted mb-2">No hay links creados</h3>
          <p className="text-subtle mb-6">Comienza agregando tu primer link externo</p>
          <button
            onClick={handleNewLink}
            className="inline-flex items-center gap-2 px-6 py-3 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg font-semibold rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Crear Primer Link
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {links.map((link, index) => {
            const Icon = getIcon(link.icono);
            return (
              <div
                key={link.id}
                className={`glass-surface rounded-xl p-6 border transition-all ${
                  link.activo
                    ? 'border-white/[0.06] hover:border-cubus-lime/40'
                    : 'border-white/[0.06] opacity-60'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${
                    link.activo ? 'bg-cubus-lime/10' : 'bg-gray-700/30'
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      link.activo ? 'text-cubus-lime' : 'text-subtle'
                    }`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white mb-1">{link.titulo}</h3>
                        {link.descripcion && (
                          <p className="text-muted text-sm mb-2">{link.descripcion}</p>
                        )}
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cubus-lime text-sm hover:underline flex items-center gap-1 break-all"
                        >
                          {link.url}
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>

                        {/* Course assignments display */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(link as any).apply_to_all_courses ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cubus-lime/10 border border-cubus-lime/30 rounded-md text-xs text-cubus-lime">
                              <ExternalLink className="w-3 h-3" />
                              Disponible en todos los cursos
                            </span>
                          ) : (
                            <>
                              {(link as any).link_course_assignments && (link as any).link_course_assignments.length > 0 ? (
                                (link as any).link_course_assignments.map((assignment: any) => (
                                  <span
                                    key={assignment.course_id}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 border border-blue-500/30 rounded-md text-xs text-blue-400"
                                  >
                                    {assignment.courses?.name || 'Curso'}
                                  </span>
                                ))
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 border border-red-500/30 rounded-md text-xs text-red-400">
                                  Sin cursos asignados
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => handleMoveUp(link, index)}
                            disabled={index === 0}
                            className="p-1 hover:bg-white/[0.04] rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Mover arriba"
                          >
                            <ChevronUp className="w-4 h-4 text-muted" />
                          </button>
                          <button
                            onClick={() => handleMoveDown(link, index)}
                            disabled={index === links.length - 1}
                            className="p-1 hover:bg-white/[0.04] rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Mover abajo"
                          >
                            <ChevronDown className="w-4 h-4 text-muted" />
                          </button>
                        </div>

                        <button
                          onClick={() => handleToggleActive(link)}
                          className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
                          title={link.activo ? 'Desactivar' : 'Activar'}
                        >
                          {link.activo ? (
                            <Eye className="w-5 h-5 text-green-500" />
                          ) : (
                            <EyeOff className="w-5 h-5 text-subtle" />
                          )}
                        </button>

                        <button
                          onClick={() => handleEdit(link)}
                          className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-5 h-5 text-blue-400" />
                        </button>

                        <button
                          onClick={() => handleDelete(link.id)}
                          disabled={deletingId === link.id}
                          className="p-2 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                          title="Eliminar"
                        >
                          {deletingId === link.id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                          ) : (
                            <Trash2 className="w-5 h-5 text-red-500" />
                          )}
                        </button>
                      </div>
                    </div>

                    {!link.activo && (
                      <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm text-yellow-500">
                          Este link no es visible para los estudiantes
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LinkModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingLink(null);
        }}
        onSave={handleSave}
        link={editingLink}
      />
    </div>
  );
}
